// Pure math for turning a region dragged over the on-screen preview into a crop of the captured frames.
//
// The preview <video> is shown with object-fit: contain inside a container whose height is capped, so the
// picture is often narrower (or shorter) than the container, with empty bars at the sides or top. The drag
// coordinates are relative to the container, so the bars must be subtracted and the scale taken from the
// picture's real displayed size, not the container size. Source pixels are the captured frame's own
// videoWidth/videoHeight, which already include any device pixel ratio, so no DPR factor is applied here.

export interface Box { x: number; y: number; w: number; h: number }

export const evenSize = (n: number) => Math.max(2, Math.round(n / 2) * 2);

/** Where the video picture actually sits inside a container when drawn with object-fit: contain. */
export function containedVideoRect(containerW: number, containerH: number, videoW: number, videoH: number): Box {
  if (!(containerW > 0) || !(containerH > 0) || !(videoW > 0) || !(videoH > 0)) {
    return { x: 0, y: 0, w: Math.max(containerW, 1), h: Math.max(containerH, 1) };
  }
  const s = Math.min(containerW / videoW, containerH / videoH);
  const w = videoW * s, h = videoH * s;
  return { x: (containerW - w) / 2, y: (containerH - h) / 2, w, h };
}

/**
 * Converts a selection (container CSS px) to an integer crop rectangle in source pixels.
 * x, y, w, h are even and always inside the source frame, and the aspect ratio of the selection is kept
 * (up to the 1 px rounding that even dimensions need).
 */
export function selectionToCrop(
  sel: Box,
  container: { w: number; h: number },
  video: { w: number; h: number },
): Box {
  const shown = containedVideoRect(container.w, container.h, video.w, video.h);
  const scaleX = video.w / shown.w;
  const scaleY = video.h / shown.h;
  // Clip the selection to the picture itself (it may start or end over the bars).
  const x0 = Math.max(sel.x, shown.x), y0 = Math.max(sel.y, shown.y);
  const x1 = Math.min(sel.x + sel.w, shown.x + shown.w), y1 = Math.min(sel.y + sel.h, shown.y + shown.h);
  const evenFloor = (n: number) => Math.max(0, Math.floor(n / 2) * 2);
  let x = evenFloor((x0 - shown.x) * scaleX);
  let y = evenFloor((y0 - shown.y) * scaleY);
  let w = evenSize(Math.max(0, x1 - x0) * scaleX);
  let h = evenSize(Math.max(0, y1 - y0) * scaleY);
  const maxW = Math.max(2, Math.floor(video.w / 2) * 2), maxH = Math.max(2, Math.floor(video.h / 2) * 2);
  w = Math.min(w, maxW); h = Math.min(h, maxH);
  x = Math.min(x, video.w - w); y = Math.min(y, video.h - h);
  x = Math.max(0, Math.floor(x / 2) * 2); y = Math.max(0, Math.floor(y / 2) * 2);
  return { x, y, w, h };
}
